import { useState } from 'react';
import { endpoints } from '../../api';
import { stagePhotoResult } from './photo-result-entry';
import { uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { useCircleDelivery } from '../studio/circle/use-circle-delivery';
import { usePhotoGallery } from './use-photo-gallery';
import { usePhotoJob } from './use-photo-job';
import { StudioOrderTail } from '../studio/StudioOrderTail';
import { studioFrameSize } from '../studio/studio-order-tail-model';
import { record } from '../studio/studio-content';
import { studioMediaUrl } from '../studio/studio-preview-model';
import { photoResultCard } from './photo-result-card';
import { useStudioOrderSourcePreview } from '../studio/use-studio-photo-previews';
import './server-photo.css';
const deliveryLabels: Record<string, string> = { photo: 'Фото', document: 'Файлом', album: 'Альбомом' };
export function PhotoOrderResult({
  state,
  labels: statusLabels,
}: {
  state: CircleFormState;
  labels?: { done: string; failed: string; running: string };
}) {
  const screen = state.form?.screen ?? state.intent?.body.screen;
  usePhotoJob(state, screen === 'collage' ? 'collage' : screen === 'avatar' ? 'avatar' : 'photo');
  const { orderId, order, busy, submissionBlocked } = state;
  const gallery = usePhotoGallery(order);
  const [selected, select] = useState<number>(0);
  const [transferError, setTransferError] = useState('');
  const delivery = useCircleDelivery(orderId ?? 0, order?.status === 'done');
  const source = useStudioOrderSourcePreview(state, 'photo', !!orderId && order?.status !== 'done');
  if (!orderId) return null;
  const active = gallery.items.find(item => item.index === selected) ?? gallery.items[0];
  const sent = ['sent', 'delivered'].includes(delivery.state ?? '');
  const many = gallery.items.length > 1;
  const inputs = (state.intent?.body.inputs ?? {}) as Record<string, unknown>;
  const count = Number(inputs.count ?? record(order).variants ?? 0);
  // «Фото готово» — только когда точно известен один вариант; иначе «Фото готовы».
  const single = gallery.items.length === 1 || (!gallery.items.length && count === 1);
  const plural = !single;
  const picture = studioMediaUrl(record(record(record(order).display).picture).url) || source;
  const ratio = typeof inputs.format === 'string' && /^\d+:\d+$/.test(inputs.format) ? inputs.format.replace(':', '/') : '3/4';
  const transfer = async (destination: 'video' | 'reels') => {
    if (!active || !order) return;
    try {
      setTransferError('');
      const me = await endpoints.me();
      if (me.user.id !== state.intent?.owner) throw new Error('owner_changed');
      stagePhotoResult({
        owner: me.user.id,
        orderId: order.id,
        revision: order.result_revision,
        index: active.index,
        file: new File([active.blob], active.name, { type: active.mime }),
        destination,
      });
    } catch {
      setTransferError('Не удалось передать выбранное фото. Попробуйте снова.');
    }
  };
  return (
    <section className="photo-order" aria-live="polite">
      <StudioOrderTail
        state={state}
        texts={{
          run: statusLabels?.running ?? (count > 1 ? `Создаём ${count} фото` : 'Создаём фото'),
          runSub: 'Можно уйти с экрана: бот пришлёт готовые фото в чат.',
          done: statusLabels?.done ?? (plural ? 'Фото готовы' : 'Фото готово'),
          doneSub: plural ? 'Бот уже прислал их в чат, в отдельную тему.' : 'Бот уже прислал его в чат, в отдельную тему.',
          donePending: plural ? 'Бот пришлёт их в чат, в отдельную тему.' : 'Бот пришлёт его в чат, в отдельную тему.',
          failed: statusLabels?.failed ?? 'Фото не создано',
          refusal: inputs.photo ? 'Заменить фото' : 'Изменить описание',
          watermark: 'на фото',
        }}
        delivered={delivery.state ? sent : undefined}
        hero={
          <div className="pr-f studio-order-frame" style={{ aspectRatio: ratio, ...studioFrameSize(ratio) }}>
            <span className="pr-pic">{picture && <i><img src={picture} alt="" /></i>}</span>
          </div>
        }
        card={photoResultCard(state)}
        actions={[
          ...(active ? [{ label: many ? `Скачать № ${active.index + 1}` : 'Скачать', href: active.url, download: active.name }] : []),
          {
            label: 'Ещё вариант',
            onClick: async () => {
              if (await state.returnToForm()) document.dispatchEvent(new CustomEvent('steos:studio:order-clear', { detail: { id: orderId } }));
            },
          },
        ]}
        next={
          active && order
            ? [
                { label: 'Оживить фото', icon: 'video', onClick: () => void transfer('video') },
                { label: 'Сделать рилс', icon: 'film', onClick: () => void transfer('reels') },
              ]
            : []
        }
      >
        {order?.status === 'uncertain' && (
          <button type="button" className="ar-lnk" disabled={busy || submissionBlocked} onClick={() => void state.refreshStatus()}>
            {uiText('Проверить статус заказа')}
          </button>
        )}
        {gallery.loading && <p role="status">{uiText('Загружаем фото…')}</p>}
        {gallery.error && (
          <p role="alert">
            {uiText('Не удалось загрузить фото.')}{' '}
            <button type="button" onClick={gallery.retry}>
              {uiText('Повторить')}
            </button>
          </p>
        )}
        {!!gallery.items.length && (
          <div className={`photo-result-grid${many ? ' many' : ''}`}>
            {gallery.items.map((item, index) => (
              <button type="button" key={item.index} aria-pressed={active?.index === item.index} onClick={() => select(item.index)}>
                <img src={item.url} alt={`${uiText('Вариант')} ${index + 1}`} />
                {many && <span className="mono">{index + 1}</span>}
              </button>
            ))}
          </div>
        )}
        {order?.status === 'done' && !order.result?.available && <p>{uiText('Результат недоступен. Проверьте историю заказа.')}</p>}
        {transferError && <p role="alert">{uiText(transferError)}</p>}
        {delivery.error && <p role="alert">{uiText(delivery.error)}</p>}
        {/* Бот присылает результат сам (§1.4); отправка вручную — только если доставка не удалась. */}
        {delivery.state === 'failed' &&
          delivery.formats
            .filter(format => Object.hasOwn(deliveryLabels, format))
            .map(format => (
              <button
                type="button"
                key={format}
                className="ar-lnk"
                disabled={busy || submissionBlocked || delivery.busy}
                onClick={() => void delivery.send(format)}
              >
                {uiText('Отправить в чат')} · {uiText(deliveryLabels[format])}
              </button>
            ))}
      </StudioOrderTail>
    </section>
  );
}
